import React, { useEffect, useState, Component } from 'react';
import { Lightbulb, Info, CheckCircle, Warning, X } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
export type CalloutVariant = 'tip' | 'info' | 'success' | 'warning';
interface CalloutProps {
  variant?: CalloutVariant;
  title?: React.ReactNode;
  children: React.ReactNode;
  /** When true, an X button hides the callout for the current session. */
  dismissible?: boolean;
  /**
   * When set together with `dismissible`, dismissal is persisted to localStorage
   * under `izio_callout_dismissed_{dismissKey}` and the callout will never
   * render again on mount once dismissed.
   */
  dismissKey?: string;
  /**
   * Optional callback fired when the user dismisses the callout via the X
   * button. Use this when dismissal needs side effects beyond the default
   * dismissKey persistence (e.g. marking related entities as acknowledged).
   */
  onDismiss?: () => void;
  className?: string;
}
const DISMISS_PREFIX = 'izio_callout_dismissed_';
const VARIANT_STYLES: Record<CalloutVariant, {
  wrapper: string;
  iconWrap: string;
  iconColor: string;
  title: string;
  body: string;
}> = {
  tip: {
    wrapper: 'bg-lime-5 border border-lime-4',
    iconWrap: 'text-lime-1',
    iconColor: 'text-lime-1',
    title: 'text-light-1',
    body: 'text-light-2'
  },
  info: {
    wrapper: 'bg-dark-2 border border-dark-3',
    iconWrap: 'text-light-3',
    iconColor: 'text-light-3',
    title: 'text-light-1',
    body: 'text-light-2'
  },
  success: {
    wrapper: 'bg-lime-5 border border-lime-4',
    iconWrap: 'text-lime-1',
    iconColor: 'text-lime-1',
    title: 'text-lime-1',
    body: 'text-light-1'
  },
  warning: {
    wrapper: 'bg-red-4 border border-red-2',
    iconWrap: 'text-red-1',
    iconColor: 'text-red-1',
    title: 'text-red-1',
    body: 'text-light-1'
  }
};
const ICON_MAP: Record<CalloutVariant, ComponentType<any>> = {
  tip: Lightbulb,
  info: Info,
  success: CheckCircle,
  warning: Warning
};
function readDismissed(key?: string): boolean {
  if (!key) return false;
  try {
    return localStorage.getItem(DISMISS_PREFIX + key) === '1';
  } catch {
    return false;
  }
}
function persistDismissed(key: string) {
  try {
    localStorage.setItem(DISMISS_PREFIX + key, '1');
  } catch {
    // noop
  }
}
export function Callout({
  variant = 'tip',
  title,
  children,
  dismissible = false,
  dismissKey,
  onDismiss,
  className
}: CalloutProps) {
  const [hidden, setHidden] = useState(() => readDismissed(dismissKey));
  // Re-check on mount in case the value was set after the initial render
  useEffect(() => {
    if (dismissKey && readDismissed(dismissKey)) setHidden(true);
  }, [dismissKey]);
  if (hidden) return null;
  const Icon = ICON_MAP[variant];
  const styles = VARIANT_STYLES[variant];
  const handleDismiss = () => {
    setHidden(true);
    if (dismissKey) persistDismissed(dismissKey);
    onDismiss?.();
  };
  return <div className={cn('relative rounded-md p-3 sm:p-4 flex items-start gap-3', styles.wrapper, className)} role={variant === 'warning' ? 'alert' : 'note'} data-id="element-2806">
      <Icon size={18} weight={variant === 'tip' ? 'fill' : 'regular'} className={cn('shrink-0 mt-0.5', styles.iconColor)} data-id="element-2807" />
      <div className="flex-1 min-w-0 text-sm leading-relaxed" data-id="element-2808">
        {title && <div className={cn('font-semibold mb-0.5', styles.title)} data-id="element-2809">
            {title}
          </div>}
        <div className={styles.body} data-id="element-2810">{children}</div>
      </div>
      {dismissible && <button type="button" onClick={handleDismiss} aria-label="Închide" className={cn('shrink-0 -mt-1 -mr-1 p-1 rounded-md transition-colors', 'text-light-3 hover:text-light-1 hover:bg-dark-3/40')} data-id="element-2811">
          <X size={14} weight="bold" data-id="element-2812" />
        </button>}
    </div>;
}